import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { algoliaService } from '../services/AlgoliaService';
import { contactService } from '../services/ContactService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import Avatar from './Avatar';

function SearchModal({ isOpen, onClose, user, onSelectNote }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [contactResults, setContactResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const searchTimeoutRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Focus on input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Small delay to ensure the modal is fully rendered
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Debounced search effect
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Clear results if query is too short
    if (searchQuery.length < 3) {
      setSearchResults([]);
      setContactResults([]);
      setError(null);
      return;
    }

    // Set searching state
    setIsSearching(true);
    setError(null);

    // Debounce search by 500ms
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        // Run note search and contact search in parallel
        const [noteResults, contacts] = await Promise.all([
            algoliaService.searchNotes(searchQuery, user.uid, 30),
            // We fetch ALL contacts and filter client side as per current strategy 
            // (ideal would be algolia for contacts too if dataset grows)
            new Promise(resolve => {
                const unsubscribe = contactService.subscribeToContacts(user.uid, (allContacts) => {
                    unsubscribe(); // one-time fetch
                    const term = searchQuery.toLowerCase();
                    const filtered = allContacts.filter(c => 
                        c.name.toLowerCase().includes(term) || 
                        (c.surname && c.surname.toLowerCase().includes(term)) ||
                        (c.relatedAccountName && c.relatedAccountName.toLowerCase().includes(term))
                    );
                    resolve(filtered);
                });
            })
        ]);
        
        // Fetch full note data for each result
        const notesWithFullData = await Promise.all(
          noteResults.map(async (result) => {
            try {
              const noteDoc = await getDoc(doc(db, 'notes', result.noteId));
              if (noteDoc.exists()) {
                return {
                  id: noteDoc.id,
                  ...noteDoc.data(),
                  // Keep Algolia highlights if available
                  _highlightResult: result._highlightResult
                };
              }
              return null;
            } catch (error) {
              console.error('Error fetching note:', error);
              return null;
            }
          })
        );

        // Filter out null results
        const validNotes = notesWithFullData.filter(note => note !== null);
        setSearchResults(validNotes);
        setContactResults(contacts);
        setIsSearching(false);
      } catch (err) {
        console.error('Search error:', err);
        setError('An error occurred while searching. Please try again.');
        setIsSearching(false);
      }
    }, 500);

    // Cleanup
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, user.uid]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const handleNoteClick = (note) => {
    onSelectNote(note);
    onClose();
    setSearchQuery(''); // Clear search after selection
  };

  const handleContactClick = (contact) => {
      navigate(`/contacts/${contact.id}`);
      onClose();
      setSearchQuery('');
  };

  const handleClose = () => {
    setSearchQuery('');
    setSearchResults([]);
    setContactResults([]);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-start justify-center pt-10 sm:pt-20"
      onClick={handleClose}
    >
      <div 
        className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Search</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close search"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Search Input - Progression.io style */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative flex items-center">
            <div className="absolute left-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search notes and contacts"
              className="block w-full pl-10 pr-24 py-2.5 border border-gray-300 rounded-lg text-sm bg-gray-50 placeholder-gray-500 focus:outline-none focus:bg-white focus:border-orange-400 focus:ring-1 focus:ring-orange-400 transition-all"
              autoComplete="off"
            />
            <div className="absolute right-3 flex items-center gap-1">
              <kbd className="hidden sm:inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 text-xs font-medium text-gray-500 bg-white border border-gray-300 rounded shadow-sm">
                ⌘
              </kbd>
              <kbd className="hidden sm:inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 text-xs font-medium text-gray-500 bg-white border border-gray-300 rounded shadow-sm">
                K
              </kbd>
            </div>
          </div>
          
          {/* Search info */}
          <div className="mt-2 text-xs text-gray-500">
            {searchQuery.length > 0 && searchQuery.length < 3 && (
              <span>Type at least 3 characters to search</span>
            )}
            {isSearching && (
              <span>Searching...</span>
            )}
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto p-4">
          {error && (
            <div className="text-center py-8">
              <p className="text-red-500 text-sm">{error}</p>
            </div>
          )}

          {!error && searchQuery.length >= 3 && !isSearching && searchResults.length === 0 && contactResults.length === 0 && (
            <div className="text-center py-8">
              <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-500">No results found for &quot;{searchQuery}&quot;</p>
            </div>
          )}

          {isSearching && (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
              <p className="mt-2 text-sm text-gray-500">Searching...</p>
            </div>
          )}

          {/* Contacts Section (Horizontal) */}
          {!isSearching && contactResults.length > 0 && (
              <div className="mb-6">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Contacts</h3>
                  <div className="flex overflow-x-auto gap-4 pb-2 scrollbar-hide">
                      {contactResults.map(contact => (
                          <div 
                            key={contact.id} 
                            onClick={() => handleContactClick(contact)}
                            className="flex flex-col items-center w-20 flex-shrink-0 cursor-pointer group"
                          >
                              <Avatar 
                                name={contact.name} 
                                surname={contact.surname} 
                                imageURL={contact.imageURL} 
                                type={contact.type}
                                size="lg" // Larger size for search view
                                className="mb-2 group-hover:ring-2 group-hover:ring-orange-200 transition-all"
                              />
                              <span className="text-xs text-center text-gray-700 font-medium truncate w-full">
                                  {contact.name}
                              </span>
                              {contact.surname && (
                                  <span className="text-xs text-center text-gray-700 font-medium truncate w-full">
                                      {contact.surname}
                                  </span>
                              )}
                          </div>
                      ))}
                  </div>
              </div>
          )}

          {/* Notes Section */}
          {!isSearching && searchResults.length > 0 && (
            <div>
                {contactResults.length > 0 && (
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Notes</h3>
                )}
                <div className="space-y-3">
                {searchResults.map((note) => (
                    <SearchResultCard
                    key={note.id}
                    note={note}
                    onClick={() => handleNoteClick(note)}
                    />
                ))}
                </div>
            </div>
          )}

          {searchQuery.length < 3 && !isSearching && (
            <div className="text-center py-8">
              <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-500">Start typing to search</p>
              <p className="mt-1 text-xs text-gray-400">Search notes by title, content, tags. Search contacts by name.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Search Result Card Component
function SearchResultCard({ note, onClick }) {
  // Get text content - prioritize summary_short, then summary
  const getText = () => {
    if (note.summary_short && note.summary_short.trim()) {
      return note.summary_short;
    } else if (note.summary && note.summary.trim()) {
      return note.summary;
    }
    return '';
  };

  return (
    <div
      onClick={onClick}
      className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md hover:border-orange-300 transition-all duration-200 cursor-pointer"
    >
      <div className="flex items-start gap-3">
        {/* Emoji */}
        {note.emoji && (
          <span className="text-2xl flex-shrink-0">{note.emoji}</span>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Title */}
          <h3 className="text-sm font-semibold text-gray-900 truncate">
            {note.title || 'Untitled Note'}
          </h3>

          {/* Summary */}
          {getText() && (
            <p className="mt-1 text-xs text-gray-600 line-clamp-2">
              {getText()}
            </p>
          )}

          {/* Tags */}
          {note.tags && note.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {note.tags.slice(0, 3).map((tag, index) => (
                <span
                  key={index}
                  className="inline-block bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full"
                >
                  {tag}
                </span>
              ))}
              {note.tags.length > 3 && (
                <span className="inline-block text-xs text-gray-400">
                  +{note.tags.length - 3} more
                </span>
              )}
            </div>
          )}
        </div>

        {/* Note Type Indicator */}
        {note.noteType === 0 && (
          <div className="flex-shrink-0">
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
              Task
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

SearchModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  onSelectNote: PropTypes.func.isRequired,
};

SearchResultCard.propTypes = {
  note: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
};

export default SearchModal;
